import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import CountrySelector from "./CountrySelector";

const categories = [
  { label: "Le Bien-être", href: "/#chapitre-01", testid: "nav-cat-bien-etre" },
  { label: "L'Éclat", href: "/#chapitre-02", testid: "nav-cat-eclat" },
  { label: "Le Mouvement", href: "/#chapitre-03", testid: "nav-cat-mouvement" },
  { label: "Le Style & le Voyage", href: "/voyage-decoration#chapitre-04", testid: "nav-cat-style" },
  { label: "La Décoration", href: "/voyage-decoration#chapitre-05", testid: "nav-cat-decoration" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-40 bg-ivory/95 border-b hairline-gold"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="/#haut" data-testid="nav-logo" className="font-serif text-xl md:text-2xl tracking-[0.25em] text-choco">
          KARYNA
        </a>
        <nav className="hidden md:flex items-center gap-10 text-xs uppercase tracking-[0.2em] text-choco/85">
          <div className="relative group">
            <a data-testid="nav-boutique" href="/#univers" className="hover:text-gold transition-colors duration-300 flex items-center gap-1.5 py-4">
              Boutique <ChevronDown size={12} className="opacity-60" />
            </a>
            <div data-testid="nav-boutique-menu" className="absolute left-1/2 -translate-x-1/2 top-full hidden group-hover:block z-50">
              <div className="bg-ivory border hairline-gold shadow-lg shadow-choco/10 py-3 w-60">
                {categories.map((c) => (
                  <a
                    key={c.href}
                    data-testid={c.testid}
                    href={c.href}
                    className="block px-6 py-2.5 text-choco/85 hover:text-gold hover:bg-gold/5 transition-colors duration-300"
                  >
                    {c.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
          <Link data-testid="nav-voyage-decoration" to="/voyage-decoration" className="hover:text-gold transition-colors duration-300">Voyage & Déco</Link>
          <a data-testid="nav-apropos" href="/#apropos" className="hover:text-gold transition-colors duration-300">À propos</a>
          <Link data-testid="nav-contact" to="/contact" className="hover:text-gold transition-colors duration-300">Contact</Link>
        </nav>
        <div className="flex items-center gap-5">
          <div className="hidden sm:block" data-testid="header-country">
            <CountrySelector variant="header" />
          </div>
          <button
            data-testid="cart-open-button"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 text-choco hover:text-denim transition-colors duration-300"
            aria-label="Ouvrir le panier"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            <span className="hidden sm:inline text-xs uppercase tracking-[0.2em]">Panier</span>
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-3 bg-gold text-ivory text-[10px] w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-choco"
            aria-label="Ouvrir le menu"
          >
            {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <nav data-testid="mobile-menu" className="md:hidden border-t hairline-gold bg-ivory px-6 py-4 space-y-1 text-xs uppercase tracking-[0.2em]">
          {categories.map((c) => (
            <a key={c.href} href={c.href} onClick={() => setMenuOpen(false)} className="block py-3 text-choco/90 border-b border-choco/5">
              {c.label}
            </a>
          ))}
          <Link to="/voyage-decoration" onClick={() => setMenuOpen(false)} className="block py-3 text-choco/90 border-b border-choco/5">Voyage & Déco</Link>
          <a href="/#apropos" onClick={() => setMenuOpen(false)} className="block py-3 text-choco/90 border-b border-choco/5">À propos</a>
          <Link to="/contact" onClick={() => setMenuOpen(false)} className="block py-3 text-choco/90">Contact</Link>
          <div className="pt-4" data-testid="mobile-menu-country">
            <CountrySelector variant="header" />
          </div>
        </nav>
      )}
    </motion.header>
  );
}
